import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import PhoneInput, {
  type Value as PhoneInputValue,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { State } from "../../";
import { EventTracker } from "../../../../Atoms/Analytics";
import InputEmail from "../../../../Atoms/InputEmail";
import styles from "../../Checkout.module.scss";
import { ValidationError } from "../SubmitButton/validation";
import NameInput, { FullName } from "./NameInput";
import { RequiredMarker } from "./RequiredMarker";

type ContactPickerProps = {
  onChange: (
    args: Partial<State> | ((prevState: State) => State),
    skipTracking?: boolean
  ) => void;
  validationErrors: ValidationError;
  state: State;
  numContacts: number;
};

export const GIVEN_NAME_KEY = "givenName" as const;
export const FAMILY_NAME_KEY = "familyName" as const;
const EMAIL_KEY = "email" as const;
const fields = ["names", "email", "confirmEmail", "contactNumber"] as const;
type Field = (typeof fields)[number];

export default function ContactPicker({
  state,
  onChange,
  validationErrors,
  numContacts,
}: ContactPickerProps) {
  const value = useMemo(
    () => ({
      names: state.names,
      email: state.email || "",
      confirmEmail: state.confirmEmail || "",
      contactNumber: state.contactNumber || "",
    }),
    [state]
  );

  const invalidKeys = useMemo(
    () =>
      validationErrors
        ? Object.keys(validationErrors.invalidArgs)
        : [validationErrors],
    [validationErrors]
  ) as string[];

  useEffect(() => {
    if (window) {
      let fname = window.localStorage.getItem("fname");
      let lname = window.localStorage.getItem("lname");
      let c_email = window.localStorage.getItem("email");
      let contact = window.localStorage.getItem("contactNumber");

      if (
        fname !== null &&
        fname !== "" &&
        lname !== null &&
        lname !== "" &&
        c_email !== null &&
        c_email !== ""
      ) {
        onChange({
          names: [
            {
              givenName: fname,
              familyName: lname,
            },
          ],
          email: c_email,
          ...(contact !== "null" &&
            contact !== null &&
            contact !== "" && { contactNumber: contact }),
        });
      }
    }
  }, [onChange]);

  const trackEvent = useCallback(
    (fieldName: string) => ({
      category: "onpage",
      action: "checkout",
      label: `checkout::${fieldName}`,
    }),
    []
  );

  const trackEvents = useMemo(
    () =>
      [GIVEN_NAME_KEY, FAMILY_NAME_KEY, EMAIL_KEY].reduce(
        (events, key) => ({
          ...events,
          key: trackEvent(key),
        }),
        {} as Record<string, ReturnType<typeof trackEvent>>
      ),
    [trackEvent]
  );

  const onChangeField = useMemo(
    () =>
      fields.reduce(
        (fns, field) => ({
          ...fns,
          [field]: <T,>(args: T) =>
            onChange((prevValue) => ({
              ...prevValue,
              [field]:
                typeof args === "function" ? args(prevValue[field]) : args,
            })),
        }),
        {} as Record<Field, Dispatch<SetStateAction<State[Field]>>>
      ),
    [onChange]
  );

  const setFullName = useCallback(
    (args: FullName | ((prevFullName: FullName) => FullName), index: number) =>
      (onChangeField["names"] as Dispatch<SetStateAction<FullName[]>>)(
        (prevNames) => [
          ...prevNames.slice(0, index),
          typeof args === "function"
            ? args(prevNames[index])
            : { ...prevNames[index], ...args },
          ...prevNames.slice(index + 1),
        ]
      ),
    [onChangeField]
  );

  const hasError = useCallback(
    (key: string) => invalidKeys.includes(key),
    [invalidKeys]
  );

  return (
    <div>
      {Array.from({ length: numContacts || 1 }, (_, index) => (
        <NameInput
          key={index}
          fullName={state.names[index]}
          setFullName={(fullName: FullName) => setFullName(fullName, index)}
          trackEvents={trackEvents}
          header={
            numContacts > 1 &&
            (index === 0 ? "Lead Traveler" : `Traveler ${index + 1}`)
          }
        />
      ))}
      <div className={styles.BookingFormFieldsetField}>
        <label htmlFor="email">
          Email {hasError(EMAIL_KEY) ? <RequiredMarker /> : null}
        </label>
        <EventTracker onBlur={trackEvents[EMAIL_KEY]}>
          <InputEmail
            id="email"
            name="email"
            autoComplete="email"
            value={value.email || ""}
            aria-invalid={hasError(EMAIL_KEY)}
            placeholder={"Email Address"}
            onChange={onChangeField["email"]}
          />
        </EventTracker>
      </div>
      <div className={styles.BookingFormFieldsetField}>
        <label htmlFor="confirmEmail">
          Confirm Email {hasError(EMAIL_KEY) ? <RequiredMarker /> : null}{" "}
        </label>
        <EventTracker onBlur={trackEvents[EMAIL_KEY]}>
          <InputEmail
            id="confirmEmail"
            name="confirmEmail"
            value={value.confirmEmail || ""}
            aria-invalid={hasError(EMAIL_KEY)}
            placeholder={"Confirm Email Address"}
            onChange={onChangeField["confirmEmail"]}
            onPaste={(e) => e.preventDefault()}
          />
        </EventTracker>
        {value.email &&
          value.confirmEmail &&
          value.email !== value.confirmEmail && (
            <span style={{ color: "red" }}>
              <b style={{ marginLeft: 5, fontSize: 12 }}>
                * Emails do not match
              </b>
            </span>
          )}
      </div>
      <div className={styles.BookingFormFieldsetField}>
        <label>
          Phone Number <small>(optional)</small>
        </label>
        <PhoneInput
          name="phone"
          value={value.contactNumber as PhoneInputValue}
          defaultCountry="US"
          placeholder="Enter phone number"
          onChange={onChangeField["contactNumber"]}
        />
      </div>
    </div>
  );
}
